// Pool entries in the Activity ledger (2026-10-21, Kholy's duplicate gold
// payout). Gold payouts, investment payouts converted to pool credit, and
// ledger carryover lines all move money through conduit accounts that are
// provisioned INACTIVE (gold_payable / invest_payable / pool_carryover), so
// the household's movement fetch — active accounts only — never saw them:
// Balances → Pool activity was the only place they showed, read-only.
//
// The rows come from v2_pool_activity (the same RPC Balances reads; it is
// scoped server-side, so an investor sees their own and an admin sees all).
// Edit and delete are admin-only and keep the record on the OTHER side in
// step: a gold payout is paired with a gold_payments row (gold Settle
// screen), an investment payout with its investment_settlements payout row.

export const POOL_ENTRY_TYPES = ['gold_bridge', 'invest_bridge', 'carryover'];
// Balances → Pool activity also lets the admin act on contributions (an
// investor's send to Kareem Trust / Kraken) — a duplicate entry has to be
// rejectable where it shows (Beko, 2026-10-21). TK-C draws stay out: they come
// from the Taekus fetcher, and the review card handles them.
export const ACTIONABLE_POOL_TYPES = [...POOL_ENTRY_TYPES, 'contribution'];

const LABELS = {
  contribution: { src: 'pool_contribution', label: 'Pool payment' },
  gold_bridge: { src: 'gold_payout', label: 'Gold payout' },
  invest_bridge: { src: 'investment_payout', label: 'Investment payout' },
  carryover: { src: 'ledger_carryover', label: 'Ledger carryover' },
};

export const poolTypeLabel = (t) => LABELS[t]?.label || t;

// One v2_pool_activity row → an Activity-ledger row (activityRows.js shape).
// Pool entries never generate value; `poolAmount` is signed (a carryover
// debit is negative), `amount` stays positive like every other row.
export function shapePoolEntry(e, ctx) {
  const meta = LABELS[e.entry_type] || { src: e.entry_type, label: e.entry_type };
  const signed = Number(e.amount || 0);
  return {
    id: 'pool-' + e.movement_id,
    movementId: e.movement_id,
    poolType: e.entry_type,
    poolAmount: signed,
    note: e.note || '',
    eventDate: e.entry_date,
    date: ctx.dateLabel(e.entry_date),
    member: ctx.holderId,
    household: ctx.holderId,
    flow: signed < 0 ? 'TK-C pool → ' + meta.label : meta.label + ' → TK-C pool',
    srcKind: meta.src, srcLabel: meta.label,
    dstKind: 'tkc_pool', dstLabel: 'TK-C pool',
    custody: null,
    amount: Math.abs(signed),
    rate: 0, value: 0, kareem: 0, investor: 0, kareemShare: 0,
    stepId: null,
    enteredByRole: 'admin',
    enteredByMemberId: null,
    movementStatus: e.status || 'confirmed',
    noValue: true,
  };
}

const LINK_TABLE = { gold_bridge: 'gold_payments', invest_bridge: 'investment_settlements' };

// The movement plus the record it is paired with. linked is null for a
// carryover line (nothing on the other side) or when the pair is gone.
export async function loadPoolEntry(sb, movementId) {
  const { data: mv, error } = await sb.from('money_movements')
    .select('id, amount, movement_date, notes, status, source, source_ref')
    .eq('id', movementId).maybeSingle();
  if (error) throw error;
  if (!mv) throw new Error('This entry no longer exists — refresh the page.');
  let linked = null;
  const table = LINK_TABLE[mv.source];
  if (table && mv.source_ref) {
    const cols = table === 'gold_payments' ? 'id, data' : 'id, amount, settle_date, status, memo';
    const { data: row } = await sb.from(table).select(cols).eq('id', mv.source_ref).maybeSingle();
    if (row) linked = { table, row };
  }
  return { mv, linked };
}

// A gold payment's pool amount is cash + expense slices (gpSyncPoolBridge).
// Only a payment whose whole pool amount is ONE cash figure can take a new
// amount from here; a split payment has to be re-cut on the gold side.
export function goldAmountEditable(data) {
  if (!data) return true;
  if (Number(data.expenseAmount || 0) > 0) return false;
  if (data.paymentType === 'mixed') return false;
  return true;
}

function notifyChanged() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('finops:enrollment-changed'));
  // The gold module (public/index.html) caches gold_payments in memory.
  window.dispatchEvent(new CustomEvent('finops:gold-changed'));
}

const today = () => new Date().toISOString().slice(0, 10);

// Edit amount / date / notes. The movement is patched in place (not via
// v2_correct_transfer, whose replacement row would drop source/source_ref
// and orphan the pair); the paired record gets the same amount and date.
export async function editPoolEntry(sb, entry, { amount, date, notes }) {
  const amt = Math.round(Number(amount) * 100) / 100;
  if (!Number.isFinite(amt) || amt <= 0) throw new Error('Enter an amount above zero.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ''))) throw new Error('Enter a date.');
  const { mv, linked } = entry;
  const amountChanged = Math.abs(amt - Number(mv.amount)) > 0.004;
  if (amountChanged && linked?.table === 'gold_payments' && !goldAmountEditable(linked.row.data)) {
    throw new Error('This gold payment is split (cash plus expenses, or BGS plus profit). Change its amount on Gold → Settle — the pool updates with it.');
  }

  const { error } = await sb.from('money_movements')
    .update({ amount: amt, movement_date: date, notes: notes?.trim() || null })
    .eq('id', mv.id);
  if (error) throw error;

  if (linked?.table === 'gold_payments') {
    const d = { ...linked.row.data, paymentDate: date };
    if (amountChanged) {
      d.cashAmount = amt;
      if (d.paymentType === 'bgs-payout') d.bgsAmount = amt;
    }
    d.poolMovementId = mv.id;
    const { error: gErr } = await sb.from('gold_payments')
      .update({ data: d, updated_at: new Date().toISOString() }).eq('id', linked.row.id);
    if (gErr) throw new Error('The pool entry was saved, but its gold payment could not be updated: ' + gErr.message);
  } else if (linked?.table === 'investment_settlements') {
    const { error: sErr } = await sb.from('investment_settlements')
      .update({ amount: amt, settle_date: date }).eq('id', linked.row.id);
    if (sErr) throw new Error('The pool entry was saved, but its payout row could not be updated: ' + sErr.message);
  }
  notifyChanged();
}

// Delete = reverse the movement through v2_reverse_transfer (the same
// transactional void every other delete uses), then retire the paired
// record: the gold payment is deleted (exactly what Del on the gold Settle
// screen does), the payout row is soft-removed (status rejected + note, the
// T-024 convention). alsoLinked=false keeps the paired record.
export async function deletePoolEntry(sb, entry, { alsoLinked = true } = {}) {
  const { mv, linked } = entry;
  if (mv.status === 'pending') {
    // Never confirmed, so nothing to reverse: reject it — the same status
    // change as "Not mine / duplicate" on the review card, through the RPC.
    const { error } = await sb.rpc('v2_set_transfer_status', {
      p_movement: mv.id, p_status: 'denied',
      p_reason: 'rejected from Balances → Pool activity (duplicate or not received)',
    });
    if (error) throw error;
  } else if (mv.status !== 'denied') {
    const { error } = await sb.rpc('v2_reverse_transfer', {
      p_movement: mv.id, p_reason_code: 'other',
      p_notes: 'pool entry deleted from the investment app' + (linked ? ' (' + linked.table + ' ' + linked.row.id + ')' : ''),
    });
    if (error) throw error;
  }
  if (alsoLinked && linked?.table === 'gold_payments') {
    const { error: gErr } = await sb.from('gold_payments').delete().eq('id', linked.row.id);
    if (gErr) throw new Error('The pool entry was removed, but its gold payment could not be deleted: ' + gErr.message);
  } else if (alsoLinked && linked?.table === 'investment_settlements' && linked.row.status !== 'rejected') {
    const memo = (linked.row.memo ? linked.row.memo + ' | ' : '') + 'Removed by Kareem ' + today() + ' with its pool credit';
    const { error: sErr } = await sb.from('investment_settlements')
      .update({ status: 'rejected', memo }).eq('id', linked.row.id);
    if (sErr) throw new Error('The pool entry was removed, but its payout row could not be retired: ' + sErr.message);
  }
  notifyChanged();
}
